import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import Icon from 'components/AppIcon';
import ProgressBar from './components/ProgressBar';
import WorkoutStep from './components/WorkoutStep';
import GoalStep from './components/GoalStep';
import DietStep from './components/DietStep';
import CompletionScreen from './components/CompletionScreen';

const TOTAL_STEPS = 3;

const defaultWorkoutData = {
  workoutTypes: [],
  experienceLevel: 'beginner',
  preferredDays: [],
  preferredTime: 'morning',
};

const defaultGoalData = {
  primaryGoal: '',
  targetWeight: '',
  timeline: '3_months',
  motivationLevel: 3,
  notes: '',
};

const defaultDietData = {
  dietaryPreference: 'no_restriction',
  allergies: [],
  calorieGoal: '',
  mealsPerDay: 3,
  waterGoal: 2.5,
  supplements: '',
};

export default function GuidedOnboardingFlow() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState('forward');
  const [animating, setAnimating] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [workoutData, setWorkoutData] = useState(defaultWorkoutData);
  const [goalData, setGoalData] = useState(defaultGoalData);
  const [dietData, setDietData] = useState(defaultDietData);
  const [errors, setErrors] = useState({});
  const [initialized, setInitialized] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  // Load from DB first, then overlay any in-progress localStorage draft
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await api.get('/profiles/me');
        if (cancelled) return;

        const prefs = data?.preferences;

        // Start with DB values
        if (prefs?.workout) setWorkoutData((p) => ({ ...p, ...prefs.workout }));
        if (prefs?.goal)    setGoalData((p)    => ({ ...p, ...prefs.goal }));
        if (prefs?.diet)    setDietData((p)    => ({ ...p, ...prefs.diet }));
      } catch (_) {}

      if (cancelled) return;

      // Overlay in-progress localStorage draft (more recent than DB)
      const saved = localStorage.getItem(`onboarding_${user?.id}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed?.workoutData) setWorkoutData((p) => ({ ...p, ...parsed.workoutData }));
          if (parsed?.goalData)    setGoalData((p)    => ({ ...p, ...parsed.goalData }));
          if (parsed?.dietData)    setDietData((p)    => ({ ...p, ...parsed.dietData }));
          if (parsed?.currentStep) setCurrentStep(parsed.currentStep);
        } catch (_) {}
      }

      setInitialized(true);
      setLoadingData(false);
    };

    load();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Auto-save to localStorage — only after initial load to avoid overwriting saved data
  const autoSave = useCallback(() => {
    if (!user?.id || !initialized) return;
    const saveData = { currentStep, workoutData, goalData, dietData };
    localStorage.setItem(`onboarding_${user?.id}`, JSON.stringify(saveData));
  }, [user?.id, initialized, currentStep, workoutData, goalData, dietData]);

  useEffect(() => {
    autoSave();
  }, [autoSave]);

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!workoutData?.workoutTypes?.length) newErrors.workoutTypes = 'Please select at least one workout type';
    }
    if (step === 2) {
      if (!goalData?.primaryGoal) newErrors.primaryGoal = 'Please select a primary goal';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const saveStep = async (step) => {
    if (!user?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (step === TOTAL_STEPS) {
        await api.patch('/profiles/me', {
          preferences: {
            workout: workoutData,
            goal: goalData,
            diet: dietData,
          },
        });
        localStorage.removeItem(`onboarding_${user?.id}`);
      }
    } catch (err) {
      setSaveError('Failed to save. Please try again.');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    await saveStep(currentStep);
    if (saveError) return;

    if (currentStep === TOTAL_STEPS) {
      setIsCompleted(true);
      return;
    }

    setDirection('forward');
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep((s) => s + 1);
      setErrors({});
      setAnimating(false);
    }, 200);
  };

  const handleBack = () => {
    if (currentStep === 1) return;
    setDirection('back');
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep((s) => s - 1);
      setErrors({});
      setAnimating(false);
    }, 200);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WorkoutStep data={workoutData} onChange={setWorkoutData} errors={errors} />;
      case 2:
        return <GoalStep data={goalData} onChange={setGoalData} errors={errors} />;
      case 3:
        return <DietStep data={dietData} onChange={setDietData} goalData={goalData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex flex-col">
      {/* Header */}
      <header className="border-b border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center">
            <Icon name="Zap" size={16} color="#fff" strokeWidth={2.5} />
          </div>
          <span className="font-bold text-[var(--color-foreground)] font-[var(--font-heading)] text-lg">
            Fit<span className="text-[var(--color-primary)]">Pulse</span>
          </span>
        </div>
        {!isCompleted && (
          <button
            onClick={() => navigate('/member-portal')}
            className="text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] font-[var(--font-caption)] flex items-center gap-1 transition-colors"
          >
            Skip for now
            <Icon name="ChevronRight" size={14} color="currentColor" />
          </button>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-start justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          {loadingData ? (
            <div className="flex items-center justify-center py-20">
              <Icon name="Loader2" size={28} color="var(--color-primary)" className="animate-spin" />
            </div>
          ) : !isCompleted ? (
            <>
              <ProgressBar currentStep={currentStep} />

              {/* Step Content */}
              <div
                className={`bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] p-6 md:p-8 shadow-[var(--shadow-md)] transition-all duration-200 ${
                  animating
                    ? direction === 'forward'
                      ? 'opacity-0 translate-x-4' :'opacity-0 -translate-x-4' :'opacity-100 translate-x-0'
                }`}
              >
                {renderStep()}

                {/* Error Banner */}
                {saveError && (
                  <div className="mt-4 flex items-center gap-2 p-3 rounded-[var(--radius-sm)] bg-[var(--color-destructive)]/10 border border-[var(--color-destructive)]/20">
                    <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
                    <p className="text-sm text-[var(--color-destructive)] font-[var(--font-caption)]">{saveError}</p>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--color-border)]">
                  <button
                    onClick={handleBack}
                    disabled={currentStep === 1}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <Icon name="ChevronLeft" size={16} color="currentColor" />
                    Back
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-[var(--radius-sm)] bg-[var(--color-primary)] text-white text-sm font-semibold font-[var(--font-caption)] hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                  >
                    {saving ? (
                      <>
                        <Icon name="Loader2" size={16} color="#fff" className="animate-spin" />
                        Saving...
                      </>
                    ) : currentStep === TOTAL_STEPS ? (
                      <>
                        Complete Setup
                        <Icon name="CheckCircle" size={16} color="#fff" />
                      </>
                    ) : (
                      <>
                        Continue
                        <Icon name="ChevronRight" size={16} color="#fff" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] p-6 md:p-8 shadow-[var(--shadow-md)]">
              <CompletionScreen
                workoutData={workoutData}
                goalData={goalData}
                dietData={dietData}
              />
            </div>
          )}
        </div>
      </main>

    </div>
  );
}
